import React, { FC, RefObject } from 'react';
import { Dayjs } from 'dayjs';
import {
  InformationCircleIcon,
  ArrowPathIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { LuCalendarDays, LuClock, LuX } from 'react-icons/lu';
import classNames from 'classnames';
import AppButton from '@/components/atoms/AppButton';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { Text } from '@/components/atoms/AppText';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { tid } from '@/utils/testId';
import { DATE_TIME_PICKER_TEST_IDS as TID } from '@/constant/testIds/dateTimePicker.testIds';
import {
  APP_BG,
  APP_BORDER,
  APP_SEMANTIC,
  APP_TEXT,
} from '@/constant/theme/colors.constant';
import { Shortcut } from './types';
import { TimeSelector } from './TimeSelector';
import { CalendarView } from './CalendarView';
import { ShortcutsPanel } from './ShortcutsPanel';

interface DatePickerPopupProps {
  containerRef: RefObject<HTMLDivElement>;
  showShortcuts: boolean;
  currentMonth: Dayjs;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  hoveredDate: Dayjs | null;
  startHour: string;
  startMinute: string;
  endHour: string;
  endMinute: string;
  minDate?: Date;
  maxDate?: Date;
  selectedShortcut?: string | null;
  onShortcutSelect: (shortcut: Shortcut) => void;
  onMonthChange: (month: Dayjs) => void;
  onDateClick: (date: Dayjs) => void;
  onDateHover: (date: Dayjs | null) => void;
  onStartHourChange: (hour: string) => void;
  onStartMinuteChange: (minute: string) => void;
  onEndHourChange: (hour: string) => void;
  onEndMinuteChange: (minute: string) => void;
  onApply: () => void;
  onCancel: () => void;
  onClear: () => void;
  timezoneInfo: {
    currentTimezone: string;
    timezoneAbbreviation: string;
    currentTime: string;
  };
}

export const DatePickerPopup: FC<DatePickerPopupProps> = ({
  containerRef,
  showShortcuts,
  currentMonth,
  startDate,
  endDate,
  hoveredDate,
  startHour,
  startMinute,
  endHour,
  endMinute,
  minDate,
  maxDate,
  selectedShortcut,
  onShortcutSelect,
  onMonthChange,
  onDateClick,
  onDateHover,
  onStartHourChange,
  onStartMinuteChange,
  onEndHourChange,
  onEndMinuteChange,
  onApply,
  onCancel,
  onClear,
  timezoneInfo,
}) => {
  const hasRange = !!(startDate && endDate);
  const fmtDate = (d: Dayjs) => d.format('MMM D, YYYY · HH:mm');
  const isMobileViewport =
    typeof window !== 'undefined' && window.innerWidth < 640;

  const getFooterMessage = () => {
    if (!startDate || !endDate) {
      return ORGANISM_PROPS.DATE_PICKER_POPUP.ENABLE_APPLY_HINT;
    }

    const startStr = startDate.format('MMM D, YYYY');
    const endStr = endDate.format('MMM D, YYYY');
    const days = Math.abs(endDate.diff(startDate, 'day')) + 1;

    return `${startStr} → ${endStr} • ${days} days`;
  };

  const isAbsolute = !!containerRef.current;

  const headerActions = (
    <div className="flex items-center gap-2">
      <Text
        as="span"
        size="xs"
        weight="semibold"
        color="muted"
        className={`hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-lg ${APP_BG.muted} border ${APP_BORDER.soft}`}
      >
        <LuClock className="w-2.5 h-2.5" />
        {timezoneInfo.timezoneAbbreviation} · {timezoneInfo.currentTime}
      </Text>
      <Text
        as="span"
        size="xs"
        weight="semibold"
        color="muted"
        className={`inline-flex sm:hidden items-center gap-1 px-2 py-1 rounded-lg ${APP_BG.muted} border ${APP_BORDER.soft}`}
      >
        <LuClock className="w-2.5 h-2.5" />
        {timezoneInfo.timezoneAbbreviation}
      </Text>
      <button
        data-testid={tid(TID.POPUP_CLOSE_BTN)}
        onClick={onCancel}
        className={`w-7 h-7 rounded-lg flex items-center justify-center ${APP_SEMANTIC.neutral.text} ${APP_SEMANTIC.neutral.hover} transition-colors`}
        title={ORGANISM_PROPS.DATE_PICKER_POPUP.CLOSE_TITLE}
      >
        <LuX className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  const rangeSubtitle = hasRange
    ? `${fmtDate(startDate!)}  →  ${fmtDate(endDate!)}`
    : ORGANISM_PROPS.DATE_PICKER_POPUP.NO_RANGE_SELECTED;

  return (
    <div
      className={
        isAbsolute
          ? 'fixed sm:absolute top-16 sm:top-full mt-2 z-50 left-2 right-2 sm:left-auto sm:right-auto w-auto sm:w-max'
          : 'w-full sm:w-max'
      }
      style={
        isAbsolute && !isMobileViewport
          ? {
            left:
              containerRef.current &&
                containerRef.current.getBoundingClientRect().right <
                window.innerWidth / 2
                ? '0'
                : 'auto',
            right:
              containerRef.current &&
                containerRef.current.getBoundingClientRect().right >=
                window.innerWidth / 2
                ? '0'
                : 'auto',
          }
          : {}
      }
    >
      <div
        className={classNames(
          `rounded-2xl overflow-hidden ${APP_BG.base} shadow-2xl ring-1 ring-black/5 dark:ring-white/5 animate-in fade-in slide-in-from-top-2 duration-200`,
        )}
      >
        <PageToolbar
          icon={<LuCalendarDays />}
          title={ORGANISM_PROPS.DATE_PICKER_POPUP.DATE_RANGE_TITLE}
          subtitle={rangeSubtitle}
          actions={headerActions}
          size="sm"
        />

        <div className="flex flex-col lg:flex-row overflow-y-auto max-h-[calc(100vh-12rem)]">
          {showShortcuts && (
            <ShortcutsPanel
              onShortcutSelect={onShortcutSelect}
              selectedShortcut={selectedShortcut}
              startDate={startDate}
              endDate={endDate}
            />
          )}

          <CalendarView
            currentMonth={currentMonth}
            startDate={startDate}
            endDate={endDate}
            hoveredDate={hoveredDate}
            minDate={minDate}
            maxDate={maxDate}
            onMonthChange={onMonthChange}
            onDateClick={onDateClick}
            onDateHover={onDateHover}
            timezoneDetails={timezoneInfo}
          />

          <TimeSelector
            startDate={startDate}
            endDate={endDate}
            startHour={startHour}
            startMinute={startMinute}
            endHour={endHour}
            endMinute={endMinute}
            onStartHourChange={onStartHourChange}
            onStartMinuteChange={onStartMinuteChange}
            onEndHourChange={onEndHourChange}
            onEndMinuteChange={onEndMinuteChange}
            timezoneDetails={timezoneInfo}
          />
        </div>

        {/* ===== FOOTER ===== */}
        <div
          className={`flex flex-col lg:flex-row items-stretch lg:items-center gap-3 px-3 sm:px-6 py-3 sm:py-4 border-t ${APP_BORDER.soft} ${APP_BG.overlay}`}
        >
          <div
            data-testid={tid(TID.POPUP_RANGE_INFO)}
            className={classNames(
              'flex items-center gap-2 px-3 py-2 rounded-lg w-full lg:w-auto',
              startDate && endDate
                ? 'bg-primary/10 border border-primary/20'
                : APP_BG.muted,
            )}
          >
            <InformationCircleIcon
              className={`w-4 h-4 flex-shrink-0 ${startDate && endDate ? APP_TEXT.primary : APP_SEMANTIC.neutral.text}`}
            />
            <Text
              as="span"
              size="base"
              weight="semibold"
              color={startDate && endDate ? 'primary' : 'muted'}
            >
              {getFooterMessage()}
            </Text>
          </div>

          <div className="flex items-center justify-end gap-2 w-full lg:w-auto lg:ml-auto flex-wrap sm:flex-nowrap">
            <AppButton
              data-testid={tid(TID.POPUP_RESET_BTN)}
              variant="primary"
              tint
              size="sm"
              icon={<ArrowPathIcon />}
              onClick={onClear}
              disabled={!startDate && !endDate}
              className="min-w-[96px] justify-center"
            >
              Reset
            </AppButton>

            <AppButton
              data-testid={tid(TID.POPUP_CANCEL_BTN)}
              variant="neutral"
              size="sm"
              icon={<XMarkIcon />}
              onClick={onCancel}
              className="min-w-[96px] justify-center"
            >
              Cancel
            </AppButton>

            <AppButton
              data-testid={tid(TID.POPUP_APPLY_BTN)}
              variant="primary"
              size="sm"
              icon={<CheckIcon />}
              onClick={onApply}
              disabled={!startDate || !endDate}
              className="min-w-[96px] justify-center"
            >
              Apply
            </AppButton>
          </div>
        </div>
      </div>
    </div>
  );
};
